import React from 'react'
import { Box } from '@mui/material'
import { type Layouts, Responsive as ResponsiveGridLayout } from 'react-grid-layout'

import { SearchCardConfigurationModal } from '../../../feature-dashboard-widgets'
import { useDomRectObserver } from '@edge/spark/utils'
import { useRoles, checkRoles, useIsAdmin, useIsGlobalAdmin, useActiveAuctionId } from '@edge/spark/io-roles'

import { widgets as widgetList } from '../../../feature-dashboard-widgets'
import { breakpoints, cols, DashboardLayout, generateLayoutsForGrid } from './DashboardLayout'
import { useIsEditingLayouts, type DashboardCardDef, useDashboardReflowStore } from '@edge/spark/io-dashboards'
import { createDialog } from '@edge/spark/feature-dialog'
import { useApolloClient } from '@apollo/react-hooks'

interface Props {
    dashboardLayout: DashboardLayout
    saveLayouts: (layouts: Layouts) => void
    saveCardConfig: (cardConfig: DashboardCardDef) => void
    saveDashboardSize: (dashboardSize: { breakpoint: string; cols: number }) => void
    deleteCardFromDashboard: (cardId: string) => void
    readonly?: boolean
}

const EmptyDashboardDisplay: React.FC = () => (
    <>
        <h3>Empty Dashboard</h3>
        <h4>Add cards to display data</h4>
    </>
)

export const Dashboard: React.VFC<Props> = ({
    dashboardLayout,
    saveLayouts,
    saveCardConfig,
    saveDashboardSize,
    deleteCardFromDashboard,
    readonly = false,
}: Props) => {
    const orgId = useActiveAuctionId()
    const client = useApolloClient()
    React.useEffect(() => {
        /** Refresh all card data if orgId changed */
        client.reFetchObservableQueries(true)
    }, [client, orgId])

    // Pull this from the layout not the source
    const widgets = dashboardLayout.cards
    const isEdgeAdmin = useIsGlobalAdmin()
    const roles = useRoles()

    const { rand } = useDashboardReflowStore()
    const containerRef = React.useRef<HTMLDivElement | null>(null)
    const width = useDomRectObserver(containerRef.current, rand)?.width ?? 300
    const [configWidget, setConfigWidget] = React.useState<null | DashboardCardDef>(null)
    const [maxCols, setMaxCols] = React.useState(0)
    const [editClicked, setEditClicked] = React.useState(false)
    const isAdmin = useIsAdmin()
    const configureCard = (widgetId: string): void => {
        // tracks when the modal was opened for detail view to change.
        setEditClicked(true)
        setTimeout(() => setEditClicked(false), 1000)
        // handle widget config modal.
        const widget = widgets.find(({ id }) => id === widgetId)
        const canEdit = isEdgeAdmin || !widget?.locked || isAdmin
        if (widget && canEdit) {
            setConfigWidget(widget || null)
        } else {
            createDialog({
                message: 'This card is locked. Contact your Auction Manager to make changes.',
                color: 'warning',
                action: 'OK',
                timeout: 10000,
            })
        }
    }

    const handleCloseConfiguration = (): void => {
        // Handle the resetting the config on the card.
        setConfigWidget(null)
    }

    const handleSaveConfiguration = (cardDef: DashboardCardDef): void => {
        handleCloseConfiguration()
        saveCardConfig(cardDef)
    }

    const handleDeleteCardFromDashboard = (cardId: string): void => {
        handleCloseConfiguration()
        deleteCardFromDashboard(cardId)
    }

    const isEditingLayouts = useIsEditingLayouts()

    const renderedLayouts = React.useMemo(
        () => generateLayoutsForGrid(dashboardLayout.cards, maxCols),
        [dashboardLayout.cards, maxCols],
    )

    const onBreakpointChange = (newBreakpoint: string, newCols: number): void => {
        setMaxCols(newCols)
        saveDashboardSize({ breakpoint: newBreakpoint, cols: newCols })
    }

    return (
        <Box ref={containerRef} sx={{ width: '100%' }}>
            {!widgets.length && <EmptyDashboardDisplay />}
            <ResponsiveGridLayout
                containerPadding={[0, 0]}
                breakpoints={breakpoints}
                cols={cols}
                width={width}
                layouts={renderedLayouts}
                rowHeight={225}
                compactType="horizontal"
                onLayoutChange={(_, newLayouts): void => {
                    saveLayouts(newLayouts) // saves to tempLayouts
                }}
                // https://github.com/STRML/react-grid-layout/issues/240
                isDraggable
                draggableHandle={isEditingLayouts ? undefined : 'do-not-show'}
                isResizable={false}
                // isDroppable={Boolean(layoutsData?.accessoryDrawerState)}
                margin={[30, 30]}
                onBreakpointChange={onBreakpointChange}
            >
                {widgets
                    .map((w) => {
                        try {
                            const foundWidget = widgetList.find(({ name }) => name === w.type)
                            const Widget = foundWidget?.component
                            if (!Widget) {
                                return null
                            }
                            if (foundWidget?.requiredRoleOption && foundWidget.requiredRoleOption?.length > 0) {
                                if (!isEdgeAdmin) {
                                    const { requiredRoleOption } = foundWidget
                                    if (!roles || roles.length === 0) {
                                        return null
                                    }
                                    const response = checkRoles(roles, requiredRoleOption)
                                    if (!response) {
                                        return null
                                    }
                                }
                            }
                            return w.config ? (
                                <div key={w.id} data-test={`${w.type}-widget`}>
                                    <Widget
                                        data-test2="dashboard-widget"
                                        config={w.config}
                                        widgetId={w.id}
                                        handleConfig={configureCard}
                                        readonly={readonly}
                                    />
                                </div>
                            ) : null
                        } catch (e) {
                            console.error(e)
                            return null
                        }
                    })
                    .filter((element) => element !== null)}
            </ResponsiveGridLayout>
            {/* Only used for editing widgets. TODO: Merge logic */}
            {configWidget && (
                <SearchCardConfigurationModal
                    widgetDef={configWidget}
                    cardEditClicked={editClicked}
                    addingWidget={false}
                    handleClose={handleCloseConfiguration}
                    handleSaveConfig={handleSaveConfiguration}
                    handleDeleteCard={handleDeleteCardFromDashboard}
                />
            )}
        </Box>
    )
}

export default Dashboard
